import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Emits only while the application document is visible and focused. A resumed
 * page refreshes once immediately; hidden or unfocused documents hold no timer.
 */
@Injectable({ providedIn: 'root' })
export class ForegroundRefreshService {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  watch(intervalMs = 5_000): Observable<void> {
    const safeIntervalMs = Math.max(1_000, Math.floor(intervalMs));
    return new Observable<void>((subscriber) => {
      const windowRef = this.document.defaultView;
      if (!windowRef) {
        subscriber.complete();
        return undefined;
      }

      let intervalId: number | null = null;
      const isForeground = () => this.document.visibilityState === 'visible' && this.document.hasFocus();
      const stop = () => {
        if (intervalId !== null) {
          windowRef.clearInterval(intervalId);
          intervalId = null;
        }
      };
      const start = (refreshImmediately: boolean) => {
        stop();
        if (!isForeground()) return;
        if (refreshImmediately) subscriber.next();
        intervalId = windowRef.setInterval(() => {
          if (!isForeground()) {
            stop();
            return;
          }
          subscriber.next();
        }, safeIntervalMs);
      };
      const handleVisibility = () => start(this.document.visibilityState === 'visible');
      const handleFocus = () => start(true);

      this.document.addEventListener('visibilitychange', handleVisibility);
      windowRef.addEventListener('focus', handleFocus);
      windowRef.addEventListener('blur', stop);
      start(false);

      return () => {
        stop();
        this.document.removeEventListener('visibilitychange', handleVisibility);
        windowRef.removeEventListener('focus', handleFocus);
        windowRef.removeEventListener('blur', stop);
      };
    });
  }
}
